import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Select } from "antd";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { getAllClass, getLevelById } from "../../api/pedagogicElement";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { useEffect, useState } from "react";
import { getAllAcademicYear } from "../../api/generalParam";
import { getStudentRegistrationByClass } from "../../api/etudiants";
import { createRequest, getAllTaskType } from "../../api/request";
import { getAllUsers } from "../../api/users";
import { useNavigate } from "react-router";

type Props = {
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

interface Year {
  id: string;
  label: string;
  status: string;
}

interface TaskForm {
  taskId: string;
  enabled: boolean;
  consigne: string;
  assignedUser: string;
}

const CreateRequestModal = ({ show, onClose, onSaved }: Props) => {
  const getModalContainer = () => {
    const modalElement = document.getElementById("create_request_modal");
    return modalElement ? modalElement : document.body; // Fallback to document.body if modalElement is null
  };

  //Initialisation de l'année academmique actuelle
  const { selectedYear } = useAcademicYear();
  const [allClasses, setAllClasses] = useState<any>([]);
  const [years, setYears] = useState<Year[]>([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<any>(null);
  const [selectedClass, setSelectedClass] = useState<any>(null);
  const [allStudents, setAllStudents] = useState<any>([]);
  const [allUserProfil, setAllUserProfil] = useState<any>([]);
  const [allTaskType, setAllTaskType] = useState<any>([]);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [tasks, setTasks] = useState<TaskForm[]>([]);

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  // Shéma pour le formulaire de re-inscription
  const requestSchema = z.object({
    academicYear: z.string().nonempty("Veuillez choisir une année académique"),
    id_class: z.string().nonempty("Veuillez choisir une classe"),
    id_studentProfil: z.string().nonempty("Veuillez choisir un étudiant"),
    priority: z.string().nonempty("Veuillez spécifier la priorité"),
    tasks: z
      .array(
        z.object({
          taskId: z.string(),
          enabled: z.boolean(),
          consigne: z.string(),
          assignedUser: z.string(),
        }),
      )
      .refine(
        (tasks) => tasks.some((t) => t.enabled),
        "Veuillez sélectionner au moins une tâche",
      )
      .refine(
        (tasks) =>
          tasks
            .filter((t) => t.enabled)
            .every((t) => t.consigne && t.assignedUser),
        "Chaque tâche activée doit avoir une consigne et un utilisateur assigné",
      ),
  });

  type FormFields = z.infer<typeof requestSchema>;

  const {
    register,
    setValue,
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof requestSchema>>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      tasks, // ⚠️ Important : synchroniser les tâches avec le state
    },
  });

  //Traitement après validation du formulaire de création d'une demande
  const onSubmit: SubmitHandler<FormFields & { tasks: TaskForm[] }> = async (
    data,
  ) => {
    try {
      const taches = data.tasks
        .filter((t) => t.enabled) // garder seulement les tâches activées
        .map((t) => ({
          id_typeTache: t.taskId, // renommer taskId
          id_userProfil: t.assignedUser, // renommer assignedUser
          consigne: t.consigne, // garder consigne
        }));
      const finalData = {
        ...data,
        taches,
        tasks: undefined,
      };

      //Envoi des datas pour création vers le backend
      const response = await createRequest(finalData);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Demande créer avec succès !"); //Toast de succès

      onClose(); // On ferme le modal

      navigate(`/request-details/${response.request.id}`);
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const fetchYears = async () => {
    try {
      const response = await getAllAcademicYear();
      const formattedYears = response.data;
      setYears(formattedYears);

      // ✅ Sélection automatique de la première année
      if (formattedYears.length > 0) {
        setValue("academicYear", formattedYears[0].id);
        setSelectedAcademicYear(formattedYears[0]);
      }

      setHasLoaded(true);
      setIsLoading(false);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les années académiques en cas d'erreur
      setYears([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await getAllClass(
        selectedAcademicYear?.id,
        buildPermissionContext(Resource.REQUEST, Action.CREATE),
      );
      const classes = response.data;
      // 🔄 Enrichir chaque classe avec son niveau
      const formattedClasses = await Promise.all(
        classes.map(async (cls: any) => {
          try {
            const levelResponse = await getLevelById(
              cls.Level?.id,
              buildPermissionContext(Resource.REQUEST, Action.CREATE),
            );

            return {
              ...cls,
              level: levelResponse.data, // 👈 niveau injecté
            };
          } catch (error: any) {
            toast.error(
              error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                "Une erreur s'est produit, Réessayer plus tard",
            );
            return {
              ...cls,
              level: null,
            };
          }
        }),
      );

      // 🔠 TRI : niveau puis classe
      const sortedClasses = formattedClasses.sort((a: any, b: any) => {
        // 1️⃣ Comparaison des niveaux
        const levelA = a.level?.label || "";
        const levelB = b.level?.label || "";

        const levelCompare = levelA.localeCompare(levelB, "fr", {
          sensitivity: "base",
        });

        if (levelCompare !== 0) return levelCompare;

        // 2️⃣ Si même niveau → comparer les classes
        const classA = a.label || "";
        const classB = b.label || "";

        return classA.localeCompare(classB, "fr", {
          sensitivity: "base",
        });
      });

      setAllClasses(sortedClasses);

      // ✅ Sélection automatique de la première classe
      if (formattedClasses.length > 0) {
        setValue("id_class", formattedClasses[0].id);
        setSelectedClass(formattedClasses[0]);
      }

      setHasLoaded(true);
      setIsLoading(false);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les classes en cas d'erreur
      setAllClasses([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await getStudentRegistrationByClass(
        selectedClass?.id,
        buildPermissionContext(Resource.REQUEST, Action.CREATE),
      );
      const students = response.data || [];

      setAllStudents(students);

      // Sélectionne le premier étudiant automatiquement s'il y en a
      if ((students || []).length > 0) {
        setValue("id_studentProfil", students[0]?.StudentProfil?.id);
        setSelectedStudent(students[0]);
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les étudiants en cas d'erreur
      setAllStudents([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  const fetchAllUserProfil = async () => {
    try {
      const response = await getAllUsers(
        buildPermissionContext(Resource.REQUEST, Action.CREATE),
      );
      const users = response || [];

      setAllUserProfil(users);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les users en cas d'erreur
      setAllUserProfil([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  const fetchAllTaskType = async () => {
    try {
      const response = await getAllTaskType(
        buildPermissionContext(Resource.REQUEST, Action.CREATE),
      );
      const taskType = response.data || [];

      setAllTaskType(taskType);

      const formattedTask = taskType.map((task: any) => ({
        taskId: task.id,
        enabled: false,
        consigne: "",
        assignedUser: "",
      }));

      setTasks(formattedTask);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les type de tache en cas d'erreur
      setAllTaskType([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;

    fetchYears();
  }, [selectedYear]);

  useEffect(() => {
    // On attend qu'une année académique soit définie
    if (!selectedAcademicYear?.id) return;

    fetchClasses();
  }, [selectedAcademicYear]);

  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!selectedClass?.id) return;

    fetchStudents();
  }, [selectedClass]);

  useEffect(() => {
    fetchAllTaskType();
    fetchAllUserProfil();
  }, []);

  const toggleTask = (taskId: string) => {
    setTasks((prev) => {
      const updated = prev.map((t) =>
        t.taskId === taskId ? { ...t, enabled: !t.enabled } : t,
      );
      setValue("tasks", updated); // 🔗 synchronisation avec RHF
      return updated;
    });
  };

  const updateConsigne = (taskId: string, value: string) => {
    setTasks((prev) => {
      const updated = prev.map((t) =>
        t.taskId === taskId ? { ...t, consigne: value } : t,
      );
      setValue("tasks", updated);
      return updated;
    });
  };

  const updateAssignedUser = (taskId: string, value: string) => {
    setTasks((prev) => {
      const updated = prev.map((t) =>
        t.taskId === taskId ? { ...t, assignedUser: value } : t,
      );
      setValue("tasks", updated);
      return updated;
    });
  };

  // 👉 Change handler pour le select des Année Académique
  const handleYearChange = (selectedId: string) => {
    const selected = years.find((c: any) => c.id.toString() === selectedId);
    if (selected) setSelectedAcademicYear(selected);
    setValue("academicYear", selectedId, { shouldValidate: true });
    setAllStudents([]); // reset students
    setSelectedStudent(null);
  };

  // 👉 Change handler pour le select des Classes
  const handleClassChange = (selectedId: string) => {
    const selected = allClasses.find(
      (c: any) => c.id.toString() === selectedId,
    );
    if (selected) setSelectedClass(selected);
    setValue("id_class", selectedId, { shouldValidate: true });
    setSelectedStudent(null);
  };

  // 👉 Change handler pour le select des Étudiants
  const handleStudentChange = (selectedId: string) => {
    const selected = allStudents.find(
      (c: any) => c.StudentProfil.id.toString() === selectedId,
    );
    if (selected) setSelectedStudent(selected);
    setValue("id_studentProfil", selectedId, { shouldValidate: true });
  };

  return (
    <>
      <Modal show={show} onHide={() => onClose()} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Nouvelle Demande</Modal.Title>
        </Modal.Header>
        <Modal.Body id="create_request_modal">
          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => clearErrors("root")}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6 g-3">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="card shadow-sm border-dark rounded">
                  <div className="card-header bg-light d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">Informations de l'étudiant</h5>
                  </div>
                  <div className="card-body pb-0 row">
                    <div className="col-xxl-4 col-xl-4 col-md-4">
                      <div className="mb-2">
                        <label
                          className="form-label mb-1"
                          htmlFor="validationServer01"
                        >
                          Année Universitaire
                        </label>
                        <Select
                          showSearch
                          optionFilterProp="label"
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toString()
                              .toLowerCase()
                              .includes(input.toString().toLowerCase())
                          }
                          size="large"
                          style={{ width: "100%" }}
                          getPopupContainer={getModalContainer}
                          placeholder="Sélectionner une année"
                          value={selectedAcademicYear?.id || undefined}
                          onChange={handleYearChange}
                          status={errors.academicYear ? "error" : undefined}
                          options={years.map((year) => ({
                            label: `Année ${year.label}`,
                            value: year.id,
                          }))}
                          className="mb-2"
                        />
                        {errors.academicYear && (
                          <div className="invalid-feedback">
                            {errors.academicYear.message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-xxl-8 col-xl-8 col-md-8">
                      <div className="mb-2">
                        <label
                          className="form-label mb-1"
                          htmlFor="validationServer01"
                        >
                          Classe
                        </label>
                        <Select
                          showSearch
                          optionFilterProp="label"
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toString()
                              .toLowerCase()
                              .includes(input.toString().toLowerCase())
                          }
                          size="large"
                          style={{ width: "100%" }}
                          getPopupContainer={getModalContainer}
                          placeholder="Sélectionner une classe"
                          value={selectedClass?.id || undefined}
                          onChange={handleClassChange}
                          status={errors.id_class ? "error" : undefined}
                          options={allClasses.map((classItem: any) => ({
                            label: `${classItem.Level.abbreviation} - ${classItem.label}`,
                            value: classItem.id,
                          }))}
                          className="mb-2"
                        />
                        {errors.id_class && (
                          <div className="invalid-feedback">
                            {errors.id_class.message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-xxl-6 col-xl-6 col-md-6">
                      <div className="mb-3">
                        <label
                          className="form-label mb-1"
                          htmlFor="validationServer01"
                        >
                          Étudiant
                        </label>
                        <Select
                          showSearch
                          optionFilterProp="label"
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toString()
                              .toLowerCase()
                              .includes(input.toString().toLowerCase())
                          }
                          size="large"
                          style={{ width: "100%" }}
                          getPopupContainer={getModalContainer}
                          placeholder="Sélectionner un étudiant"
                          value={selectedStudent?.StudentProfil.id || undefined}
                          onChange={handleStudentChange}
                          status={errors.id_studentProfil ? "error" : undefined}
                          options={allStudents.map((student: any) => ({
                            label: `${student.StudentProfil.lastname} ${student.StudentProfil.firstname}`,
                            value: student.StudentProfil.id,
                          }))}
                          className="mb-2"
                        />
                        {errors.id_studentProfil && (
                          <div className="invalid-feedback">
                            {errors.id_studentProfil.message}
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedStudent && (
                      <div className="col-xxl-3 col-xl-3 col-md-3">
                        <div className="mb-2">
                          <label
                            className="form-label mb-1"
                            htmlFor="validationServer01"
                          >
                            N° Matricule
                          </label>
                          <input
                            type="text"
                            disabled
                            value={
                              selectedStudent.StudentProfil.matricule || ""
                            }
                            className={"form-control"}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* SECTION TÂCHES */}
              <div className="col-xxl-12 col-xl-12 col-md-12 mt-0">
                <div className="card shadow-sm border-dark rounded">
                  <div className="card-header bg-light d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">Demandes à traiter</h5>
                    <div className="">
                      <select {...register("priority")} className="form-select">
                        <option value="Normal">Normal</option>
                        <option value="Urgent">Urgent</option>
                        <option value="Très urgent">Très urgent</option>
                      </select>
                    </div>
                  </div>
                  <div className="card-body pb-0">
                    {allTaskType.map((taskType: any) => {
                      const task = tasks.find((t) => t.taskId === taskType.id);

                      if (!task) return null;

                      return (
                        <div className="mb-3" key={taskType.id}>
                          <div className="form-check form-check-md">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={taskType.id}
                              checked={task.enabled}
                              onChange={() => toggleTask(taskType.id)}
                            />
                            <label
                              className="form-check-label fw-semibold"
                              htmlFor={taskType.id}
                            >
                              {taskType.label}
                            </label>
                          </div>

                          {task.enabled && (
                            <div className="row g-2 ms-4 mt-1">
                              <div className="col-md-8">
                                <textarea
                                  className="form-control"
                                  rows={1}
                                  placeholder={`Consignes pour ${taskType.label.toLowerCase()}...`}
                                  value={task.consigne}
                                  onChange={(e) =>
                                    updateConsigne(taskType.id, e.target.value)
                                  }
                                />
                              </div>

                              <div className="col-md-4">
                                <div className="input-group">
                                  <span className="input-group-text">
                                    <i className="fas fa-user-tag" />
                                  </span>
                                  <select
                                    className="form-select"
                                    value={task.assignedUser}
                                    onChange={(e) =>
                                      updateAssignedUser(
                                        taskType.id,
                                        e.target.value,
                                      )
                                    }
                                  >
                                    <option value="">— Assigner à —</option>
                                    {allUserProfil.map((user: any) => (
                                      <option key={user.id} value={user.id}>
                                        {user.lastname} {user.firstname}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CreateRequestModal,
  [buildPermission(Resource.REQUEST, Action.CREATE)],
  "masquer",
);
